
import React, { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { AIStaffMember } from "@/lib/types";
import { logger } from "@/lib/logger";
import LoadingState from "./common/LoadingState";
import NoStaffWarning from "./warnings/NoStaffWarning";
import { useQuery } from "@tanstack/react-query";
import { staffApiClient } from "@/api/staff";
import { useUserBusiness } from "@/hooks/staff/useUserBusiness";
import { useStaffConverter } from "@/hooks/staff/useStaffConverter";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { TabsContent } from "@/components/ui/tabs";
import TabsContainer from "./tabs/TabsContainer";
import { getIntegrationsByCategory } from "./registry/IntegrationsRegistry";

interface IntegrationsPanelProps {
  aiStaff?: AIStaffMember;
}

const IntegrationsPanel: React.FC<IntegrationsPanelProps> = ({ aiStaff: propAiStaff }) => {
  const { toast } = useToast();
  const [activeIntegrationTab, setActiveIntegrationTab] = useState("instagram");
  
  const { fetchCurrentUser, fetchBusinessId } = useUserBusiness();
  const { convertStaffData } = useStaffConverter();
  
  // Get current user with increased stale time to prevent unnecessary refetches
  const { data: userId, isLoading: isUserLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });
  
  // Get business ID only if we have a user ID
  const { data: businessId, isLoading: isBusinessLoading } = useQuery({
    queryKey: ['businessId', userId],
    queryFn: () => fetchBusinessId(userId!),
    enabled: !!userId,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Fetch staff data only if we have a business ID or prop staff
  const { data: staffData, isLoading: isStaffLoading } = useQuery({
    queryKey: ['primaryAiStaff', businessId],
    queryFn: async () => {
      if (!businessId) return propAiStaff || null;
      
      try {
        const allStaff = await staffApiClient.fetchAllStaff(businessId);
        if (!allStaff || allStaff.length === 0) return propAiStaff || null;
        return convertStaffData(allStaff[0]);
      } catch (error) {
        logger.error({
          message: "Error fetching primary AI staff",
          error,
          context: { businessId }
        });
        return propAiStaff || null;
      }
    },
    enabled: !!businessId || !!propAiStaff,
    staleTime: 300000, // 5 minutes
    initialData: propAiStaff || undefined,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  
  // Loading state
  const isLoading = (isUserLoading || isBusinessLoading || isStaffLoading) && !propAiStaff;

  if (isLoading) {
    return <LoadingState />;
  }

  const aiStaffId = staffData?.id || "sample-1";
  const aiStaffName = staffData?.name || "Your AI Assistant";

  // Get business integrations from registry
  const businessIntegrations = getIntegrationsByCategory("business");
  
  // Create tab items from registered integrations
  const tabs = businessIntegrations.map(integration => ({
    id: integration.id,
    icon: integration.icon,
    label: integration.name,
    disabled: !integration.isAvailable
  }));

  return (
    <div className="space-y-6">
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Business Integrations</CardTitle>
          <CardDescription>Connect your AI assistants to external business tools and services</CardDescription>
        </CardHeader>
        <CardContent>
          <NoStaffWarning showWarning={!staffData} />
          
          {!staffData ? null : (
            <TabsContainer 
              activeTab={activeIntegrationTab} 
              setActiveTab={setActiveIntegrationTab} 
              tabs={tabs}
            >
              {businessIntegrations.map(integration => (
                <TabsContent key={integration.id} value={integration.id}>
                  {integration.isAvailable ? (
                    <integration.component 
                      aiStaffId={aiStaffId} 
                      aiStaffName={aiStaffName} 
                    />
                  ) : (
                    <Card>
                      <CardContent className="py-6 text-center">
                        <h3 className="text-lg font-medium mb-2">{integration.name} - Coming Soon</h3>
                        <p className="text-muted-foreground">
                          We're working on integrating {integration.name}. Check back later!
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              ))}
            </TabsContainer>
          )}
          
          {!staffData && (
            <div className="py-8 text-center text-muted-foreground">
              <p className="mb-2">Business tool integrations coming soon!</p>
              <p className="text-sm">We're working on integrations with popular CRMs, analytics platforms, and more.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IntegrationsPanel;
